import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useUserState,
  useUserDispatch,
} from '@/components/context/UserContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import { AlertTriangle } from 'lucide-react';
import useFetch from 'use-http';
import { MaterialIcon } from '@/components/ui/shared/materialIcon';
import UserIcon from '@/v2/assets/images/User icon.png';
import { Separator } from '@/components/ui/separator';
import DiscountCards from './DiscountCards';
import GeneralPractitioner from './GeneralPactitioner';
import { useEffectOnce } from 'react-use';

const EMAIL_CHANGED_KEY = 'app:user:emailChanged';

const PersonalInfo = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userState = useUserState();
  const dispatch = useUserDispatch();

  const lastHandledTsRef = useRef(0);

  const userAccountEditEnabled =
    process.env.REACT_APP_USER_ACCOUNT_EDIT_ENABLED === 'true';

  const { post: deleteNewEmail } = useFetch(`/UserAccount/DeleteNewEmail`);
  const { get: getUserEmail } = useFetch(`/UserAccount/GetUserEmails`);

  // ---- shared fetch logic ----
  const fetchUserEmail = async () => {
    try {
      const response = await getUserEmail();

      dispatch({
        type: 'UPDATE',
        data: {
          email: response.email,
          newEmail: response.newEmail,
        },
      });
    } catch {
      // optional logging
    }
  };

  // ---- existing behavior (UNCHANGED) ----
  useEffectOnce(() => {
    if (!userState.espbiEsi && !!userState.newEmail) {
      fetchUserEmail();
    }
  });

  // ---- NEW: cross-tab email change listener ----
  useEffect(() => {
    const handler = (event) => {
      if (event.key !== EMAIL_CHANGED_KEY || !event.newValue) return;

      try {
        const { ts } = JSON.parse(event.newValue);

        // prevent duplicate handling
        if (!ts || ts <= lastHandledTsRef.current) return;
        lastHandledTsRef.current = ts;

        // respect existing rules
        if (!userState.espbiEsi) {
          fetchUserEmail();
        }
      } catch {
        // ignore malformed payloads
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [userState.espbiEsi]);

  const displayName = userState.espbiEsi
    ? userState.contacts?.find((x) => x.espbiEsi === userState.espbiEsi)?.name
    : userState.firstName;

  const displaySurname = userState.espbiEsi
    ? userState.contacts?.find((x) => x.espbiEsi === userState.espbiEsi)
        ?.surname
    : userState.lastName;

  const hasPractitionerFullName =
    !!userState.subscriptionInfo?.subscriptionPractitionerName &&
    !!userState.subscriptionInfo?.subscriptionPractitionerSurname;

  const hasPractitionerUrlData =
    !!userState.subscriptionInfo?.subscriptionPractitionerLocalId &&
    !!userState.subscriptionInfo?.subscriptionPractitionerServiceTypeId;

  const showGP = hasPractitionerFullName || hasPractitionerUrlData;
  const showDiscount = !!userState.cardNumber;
  const showAny = showGP || showDiscount;

  const handleDeleteEmailLink = async () => {
    await deleteNewEmail();
    dispatch({
      type: 'UPDATE',
      data: {
        newEmail: null,
      },
    });
  };

  return (
    <Card className="rounded-2xl shadow-sm">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Heading variant="userAccountTitle">
            {!userState.espbiEsi
              ? t('userAccount.userInfoTab.userInfoTitle')
              : 'Vaiko informacija'}
          </Heading>
          {!userState.espbiEsi && (
            <p className="text-sm text-muted-foreground mt-1">
              Jūsų asmeniniai duomenys
            </p>
          )}
        </div>

        {userAccountEditEnabled && !userState.espbiEsi && (
          <div className="hidden sm:block">
            <Button
              variant="light"
              className="rounded-full"
              onClick={() => history.push('/userAccount/personal-info/edit')}
            >
              <MaterialIcon icon="edit" size={16} className="mr-2" />
              {t('userAccount.userInfoTab.edit')}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="text-sm sm:flex sm:items-center sm:gap-6">
        <div className="hidden sm:block flex-shrink-0">
          <img
            src={UserIcon}
            alt="User avatar"
            className="w-[118px] h-[118px] object-contain"
          />
        </div>

        <div className="space-y-3 sm:space-y-1 sm:leading-6">
          <div>
            <span className="text-muted-foreground">
              {t('userAccount.userInfoTab.name')}
            </span>{' '}
            <span className="font-medium">{displayName}</span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('userAccount.userInfoTab.surname')}
            </span>{' '}
            <span className="font-medium">{displaySurname}</span>
          </div>

          {!userState.espbiEsi && (
            <div>
              <span className="text-muted-foreground">
                {t('userAccount.userInfoTab.email')}
              </span>{' '}
              <span className="font-medium">{userState.email}</span>
              {userState.newEmail && (
                <span className="sm:pl-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteEmailLink}
                    className="px-2 text-sm"
                  >
                    {t('userAccount.userInfoTab.deleteEmail')}
                  </Button>
                </span>
              )}
            </div>
          )}

          {!userState.espbiEsi && (
            <div>
              <span className="text-muted-foreground">
                {t('userAccount.userInfoTab.phone')}
              </span>{' '}
              <span className="font-medium">{userState.phoneNumber}</span>
              {!userState.phoneNumberConfirmed && (
                <div className="mt-1 flex items-start gap-2 text-sm text-orange-600">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <span>{t('validation.phoneNotConfirmed')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {userAccountEditEnabled && !userState.espbiEsi && (
          <div className="block sm:hidden pt-4">
            <Button
              variant="light"
              className="w-full rounded-full"
              onClick={() => history.push('/userAccount/personal-info/edit')}
            >
              <MaterialIcon icon="edit" size={16} className="mr-2" />
              {t('userAccount.userInfoTab.edit')}
            </Button>
          </div>
        )}
      </CardContent>

      {showAny && (
        <>
          <CardContent className="px-0">
            <Separator className="my-4" />
          </CardContent>

          <CardContent className="flex flex-col gap-6">
            {showGP && <GeneralPractitioner />}
            {showDiscount && <DiscountCards />}
          </CardContent>
        </>
      )}

      <CardFooter />
    </Card>
  );
};

export default PersonalInfo;
